import React, { useState } from "react";
import { toast } from "sonner";
import { BarChart3 } from "lucide-react";
import { api, API, getToken } from "@/lib/api";
import { inr, num } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { usePeriod, PeriodBar, FranchisePicker, ExportButtons, StatCard, ReportSection, FilterCard } from "./ReportShell";

export default function FranchiseReportTab() {
  const per = usePeriod();
  const [fr, setFr] = useState(null);
  const [rep, setRep] = useState(null);
  const [loading, setLoading] = useState(false);

  const params = () => per.buildParams({ franchise_id: fr?.id });

  const generate = async () => {
    if (!fr) { toast.error("Select a Franchise Partner"); return; }
    setLoading(true);
    try { const { data } = await api.get(`/reports/franchise?${params()}`); setRep(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to generate"); } finally { setLoading(false); }
  };

  const download = (fmt) => {
    const p = params(); p.set("fmt", fmt); p.set("token", getToken() || "");
    window.open(`${API}/reports/franchise/export?${p}`, "_blank");
  };

  const s = rep?.summary;
  return (
    <div>
      <FilterCard>
        <div className="mb-4"><FranchisePicker fr={fr} setFr={(v) => { setFr(v); setRep(null); }} /></div>
        <PeriodBar {...per} />
        <Button className="mt-4" onClick={generate} disabled={loading || !fr} data-testid="generate-report-btn"><BarChart3 className="mr-1.5 h-4 w-4" />{loading ? "Generating…" : "Generate Report"}</Button>
      </FilterCard>

      {s && (
        <div className="space-y-5" data-testid="report-result">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-muted-foreground">{s.franchise_name} · {s.referral_number} · {s.location} · {s.period_from} to {s.period_to}</p>
            <ExportButtons onDownload={download} />
          </div>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <StatCard l="Total DPs" v={num(s.total_dps)} /><StatCard l="Active DPs" v={num(s.active_dps)} />
            <StatCard l="Total Orders" v={num(s.total_orders)} /><StatCard l="DP Earnings" v={inr(s.total_dp_earnings)} accent="emerald" />
            <StatCard l="Franchise Commission" v={inr(s.franchise_commission)} accent="violet" /><StatCard l="Incentives" v={inr(s.total_incentives)} accent="amber" />
            <StatCard l="Total Payable" v={inr(s.total_payable)} accent="primary" /><StatCard l="Avg Orders/DP" v={s.avg_orders_per_dp} />
          </div>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
            <StatCard l="Total Paid" v={inr(s.total_paid)} accent="emerald" /><StatCard l="Pending Withdrawals" v={inr(s.pending_withdrawals)} accent="amber" /><StatCard l="Total Withdrawals" v={inr(s.total_withdrawals)} />
          </div>

          <ReportSection title="Delivery Partner Performance">
            <Table><TableHeader><TableRow className="hover:bg-transparent"><TableHead>DP Name</TableHead><TableHead>FE ID</TableHead><TableHead>Location</TableHead><TableHead>Model</TableHead><TableHead className="text-right">Orders</TableHead><TableHead className="text-right">Rate</TableHead><TableHead className="text-right">Base</TableHead><TableHead className="text-right">Incentives</TableHead><TableHead className="text-right">Total</TableHead><TableHead className="text-right">Commission</TableHead></TableRow></TableHeader>
              <TableBody>{rep.dp_rows.length === 0 ? <TableRow><TableCell colSpan={10} className="text-center text-muted-foreground">No activity in this period</TableCell></TableRow> : rep.dp_rows.map((r, i) => (
                <TableRow key={i} data-testid={`dp-perf-${r.fe_id}`}><TableCell className="font-semibold">{r.dp_name}</TableCell><TableCell className="font-mono text-xs">{r.fe_id}</TableCell><TableCell>{r.location}</TableCell><TableCell className="text-xs">{r.earning_model}</TableCell><TableCell className="text-right tabular">{r.orders}</TableCell><TableCell className="text-right tabular">{inr(r.rate)}</TableCell><TableCell className="text-right tabular">{inr(r.base_earnings)}</TableCell><TableCell className="text-right tabular">{inr(r.incentives)}</TableCell><TableCell className="text-right tabular font-semibold text-emerald-500">{inr(r.total_earnings)}</TableCell><TableCell className="text-right tabular text-violet-500">{inr(r.franchise_commission)}</TableCell></TableRow>
              ))}</TableBody></Table>
          </ReportSection>

          <ReportSection title="Daily Breakdown">
            <Table><TableHeader><TableRow className="hover:bg-transparent"><TableHead>Date</TableHead><TableHead className="text-right">Active DPs</TableHead><TableHead className="text-right">Orders</TableHead><TableHead className="text-right">DP Earnings</TableHead><TableHead className="text-right">Incentives</TableHead><TableHead className="text-right">Commission</TableHead></TableRow></TableHeader>
              <TableBody>{rep.daily_rows.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">No data</TableCell></TableRow> : rep.daily_rows.map((r, i) => (
                <TableRow key={i}><TableCell>{r.date}</TableCell><TableCell className="text-right tabular">{r.active_dps}</TableCell><TableCell className="text-right tabular">{r.orders}</TableCell><TableCell className="text-right tabular">{inr(r.earnings)}</TableCell><TableCell className="text-right tabular">{inr(r.incentives)}</TableCell><TableCell className="text-right tabular">{inr(r.commission)}</TableCell></TableRow>
              ))}</TableBody></Table>
          </ReportSection>
        </div>
      )}
    </div>
  );
}
